import { Injectable, Injector } from '@angular/core';
import { ApplicationService } from '@app/shared/application/application.service';
import { Observable, of, ReplaySubject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { SelectItem } from '@node_modules/primeng/api';
import { AppComponentBase } from '@shared/common/app-component-base';
import { AppConsts } from '@shared/AppConsts';
import { AlaApplicantPersonDto, AlaApplicantPersonServiceProxy, AlaContactDto, AlaContactServiceProxy, AlaDocumentServiceProxy, AlaDocumentsOutput, AlaDto, AlaInvoiceAddressDto, AlaInvoiceAddressServiceProxy, AlaPersonDto, AlaPersonServiceProxy, AlapServiceProxy, AlaStatus, ApplicationType, ApplicationUploadFilesInput, CreateAlaInput, CreateAlaOutput, CreateOrUpdateAlaContact, CreateOrUpdateAlaPerson, ExternalSystemEnum, GeneralDocumentGroupDto, GeneralFileDto, GeneralFileInfoDto, GetAlaDocumentsInput, GetAlaFileInput, GetAlaOutput, LiaTypeServiceProxy, AlaAcademicTitleDto, CreateOrUpdateAlaAcademicTitleInput, CreateOrUpdateAlaAcademicTitleOutput, AlaAcademicTitleServiceProxy, AlaBusinessDto, AlaBusinessServiceProxy, AlaApplicantSponsorshipDto, CreateOrUpdateAlaApplicantSponsorship, AlaApplicantSponsorshipServiceProxy, AlaEmploymentServiceProxy, AlaEmploymentDto, AlaMedicalFieldDirectorDto, AlaMedicalFieldDirectorServiceProxy, CreateOrUpdateAlaMedicalFieldDirector, ProfessionalLicenceDto, ApplicationStatus, PersonApplicationSummaryDto, AlaMedicalFieldServiceProxy, AlaMedicalFieldDto, CreateOrUpdateAlaMedicalFieldInput, CreateOrUpdateAlaMedicalFieldOutput, AlaApplicantContactServiceProxy, AlaApplicantContactDto, CreateOrUpdateAlaApplicantContact, GetAlaSubItemDocumentsInput, GetAlaInvoiceAddressOutput, InvoiceAddressDetailDto, GetAlaEmploymentOutput, AlaLanguageSkillDto, CreateOrUpdateAlaLanguageSkillInput, AlaPostgraduateTitleDto, CreateOrUpdateAlaPostgraduateTitleInput, CreateOrUpdateAlaPostgraduateTitleOutput, AlaDiplomaServiceProxy, AlaLanguageSkillServiceProxy, AlaPostgraduateTitleServiceProxy, AlaDiplomaDto, CreateOrUpdateAlaDiploma, AlaRestrictionsServiceProxy, CreateOrUpdateAlaRestrictionsInput, LegitimationPersonWithoutRegisterDto } from './../../../shared/service-proxies/service-proxies';
import { CreateOrUpdateAlaEmploymentInput } from '../../../shared/service-proxies/service-proxies';
import { AlaRestrictionOutput } from './../../../shared/service-proxies/service-proxies';

@Injectable({providedIn: 'root'})
export class AlaService extends AppComponentBase {

    ala: AlaDto;
    alaResultDocuments: GeneralFileInfoDto[];

    private alapsForPersonSubject = new ReplaySubject<PersonApplicationSummaryDto[]>(1);
    readonly alapsForPerson$ = this.alapsForPersonSubject.asObservable();

    constructor(
        injector: Injector,
        private applicationService: ApplicationService,
        private liaService: LiaTypeServiceProxy,
        private alapService: AlapServiceProxy,
        private alaApplicantPersonServiceProxy: AlaApplicantPersonServiceProxy,
        private alaApplicantSponsorshipServiceProxy: AlaApplicantSponsorshipServiceProxy,
        private alaApplicantContactServiceProxy: AlaApplicantContactServiceProxy,
        private alaPersonServiceProxy: AlaPersonServiceProxy,
        private alaContactServiceProxy: AlaContactServiceProxy,
        private alaDiplomaServiceProxy: AlaDiplomaServiceProxy,
        private alaRestrictionsServiceProxy: AlaRestrictionsServiceProxy,
        private alaLanguageSkillServiceProxy: AlaLanguageSkillServiceProxy,
        private alaPostgraduateTitleServiceProxy: AlaPostgraduateTitleServiceProxy,
        private alaBusinessServiceProxy: AlaBusinessServiceProxy,
        private alaEmploymentServiceProxy: AlaEmploymentServiceProxy,
        private invoiceAddressService: AlaInvoiceAddressServiceProxy,
        private alaAcademicTitleServiceProxy: AlaAcademicTitleServiceProxy,
        private alaMedicalFieldServiceProxy: AlaMedicalFieldServiceProxy,
        private alaMedicalFieldDirectorServiceProxy: AlaMedicalFieldDirectorServiceProxy,
        private documentService: AlaDocumentServiceProxy,
    ) {
        super(injector);
    }

    loadAlapsForPerson(gln?: string, ahv?: string): Observable<PersonApplicationSummaryDto[]> {
        if (gln || ahv) {
            return this.alapService.getAlapsForPerson(gln ?? undefined, ahv ?? undefined).pipe(
                tap(alaps => this.alapsForPersonSubject.next(alaps))
            );
        } else {
            this.alapsForPersonSubject.next([]);
            return of([]);
        }
    }

    getExternalSystemByProfessionId(professionId: string): Observable<ExternalSystemEnum> {
        return this.liaService.getExternalRegisterByProfessionId(professionId);
    }

    createAlap(input: CreateAlaInput): Observable<CreateAlaOutput> {
        return this.alapService.createAlap(input).pipe(tap((output: CreateAlaOutput) => {
            this.ala = null;
        }));
    }

    deleteAla(): Observable<void> {
        return this.applicationService.deleteApplication();
    }

    getAla(caseId: string): Observable<GetAlaOutput> {
        return this.alapService.getAlap(caseId).pipe(tap((output: GetAlaOutput) => {
            this.ala = output.ala;
        }));
    }

    closeAla(): void {
        this.ala = null;
    }

    getMyAlaps(): Observable<PersonApplicationSummaryDto[]> {
        return this.alapService.getMyAlaps();
    }

    isInAlaStatus(expectedStatus: AlaStatus): boolean {
        return this.ala.statusId === expectedStatus;
    }

    getPersonalLicenceForAla(): Observable<ProfessionalLicenceDto> {
        return this.alapService.getLicenceForAla(this.caseId);
    }

    releaseAla(comment: string): Observable<void> {
        switch (this.applicationService.type) {
            case ApplicationType.Alap:
                return this.alapService.releaseAlap(this.applicationService.caseId, comment);
        }
    }

    getNextStatus(status: AlaStatus): Observable<AlaStatus> {
        let isAuthority = this.permission.isGranted('Pages.Authority.Applications');
        return this.alapService.getNextStatus(this.caseId, status).pipe(map(status => {
            // Authority goes to 'finish review' and not to 'release'
            if (isAuthority && status === AlaStatus.Release) {
                return AlaStatus.FinishReview;
            }
            return status;
        }));
    }

    getStepUrl(nextStep: AlaStatus): string {
        return this.applicationService.getNextStep(AlaService.getUrlForStep(nextStep));
    }

    getFile(step: AlaStatus, id: number): Observable<GeneralFileDto> {
        const input = new GetAlaFileInput();
        input.caseId = this.caseId;
        input.step = step;
        input.id = id;
        return this.documentService.getFile(input);
    }

    uploadFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.documentService.uploadFiles(input);
    }

    deleteFile(step: AlaStatus, id: number): Observable<void> {
        return this.documentService.deleteFile(this.caseId, step, id);
    }

    get caseId(): string {
        return this.applicationService.caseId;
    }

    get status(): ApplicationStatus {
        return this.applicationService.status;
    }

    get professionId(): string {
        return this.ala?.professionId;
    }

    get sponsorshipTypeId(): string {
        return this.ala?.sponsorshipTypeId;
    }

    getApplicantName(): Observable<string> {
        return of(this.ala.application.name);
    }

    setApplicantPersonStepToReview() {
        this.applicationService.setStepToReview(AlaStatus.ApplicantPerson);
    }


    getApplicantPerson(): Observable<AlaApplicantPersonDto> {
        return this.alaApplicantPersonServiceProxy.get(this.caseId);
    }

    performRegisterCheck(caseId = this.caseId): Observable<AlaApplicantPersonDto> {
        this.applicationService.setStepToReview(AlaStatus.ApplicantPerson);
        return this.alaApplicantPersonServiceProxy.performRegisterCheck(caseId);
    }

    acceptRegisterCheck(): Observable<AlaStatus> {
        this.applicationService.setStepToReview(AlaStatus.ApplicantPerson);
        return this.alaApplicantPersonServiceProxy.acceptRegisterCheck(this.caseId).pipe(
            tap((status: AlaStatus) => {
                this.ala.currentStep = status;
            })
        );
    }

    getApplicantSponsorship(): Observable<AlaApplicantSponsorshipDto> {
        return this.alaApplicantSponsorshipServiceProxy.get(this.caseId);
    }

    getApplicantSponsorshipFiles(caseId = this.caseId): Observable<GeneralDocumentGroupDto[]> {
        const input = new GetAlaDocumentsInput();
        input.step = AlaStatus.ApplicantSponsorship;
        input.caseId = caseId;
        return this.documentService.getDocuments(new GetAlaDocumentsInput(input)).pipe(
            map((output: AlaDocumentsOutput) => output.documents)
        );
    }

    createOrUpdateApplicantSponsorship(input: CreateOrUpdateAlaApplicantSponsorship): Observable<void> {
        this.applicationService.setStepToReview(AlaStatus.ApplicantSponsorship);
        return this.alaApplicantSponsorshipServiceProxy.createOrUpdate(input).pipe(tap((status: AlaStatus) => {
            this.ala.currentStep = status;
        }))
        .pipe(map((status: AlaStatus) => null));
    }

    getApplicantContact(): Observable<AlaApplicantContactDto> {
        return this.alaApplicantContactServiceProxy.get(this.caseId);
    }

    createOrUpdateApplicantContact(input: CreateOrUpdateAlaApplicantContact): Observable<void> {
        this.applicationService.setStepToReview(AlaStatus.ApplicantContact);
        return this.alaApplicantContactServiceProxy.createOrUpdate(input).pipe(
            tap((status: AlaStatus) => {
                this.ala.currentStep = status;
            }),
            map((status: AlaStatus) => null)
        );
    }

    getPersonFiles(forceRefresh?: boolean, caseId = this.caseId): Observable<GeneralDocumentGroupDto[]> {
        const input = new GetAlaDocumentsInput();
        input.step = AlaStatus.Person;
        input.caseId = caseId;
        return this.documentService.getDocuments(new GetAlaDocumentsInput(input)).pipe(
            map((output: AlaDocumentsOutput) => output.documents)
        );
    }

    getPerson(): Observable<AlaPersonDto> {
        return this.alaPersonServiceProxy.get(this.caseId);
    }

    createOrUpdatePerson(input: CreateOrUpdateAlaPerson): Observable<void> {
        this.applicationService.setStepToReview(AlaStatus.Person);
        return this.alaPersonServiceProxy.createOrUpdate(input).pipe(
            tap((status: AlaStatus) => {
                this.ala.currentStep = status;
            }),
            map((status: AlaStatus) => null)
        );
    }

    getMedicalFieldDirector(): Observable<AlaMedicalFieldDirectorDto> {
        return this.alaMedicalFieldDirectorServiceProxy.get(this.caseId);
    }

    createOrUpdateMedicalFieldDirector(input: CreateOrUpdateAlaMedicalFieldDirector): Observable<void> {
        this.applicationService.setStepToReview(AlaStatus.MedicalFieldDirector);
        return this.alaMedicalFieldDirectorServiceProxy.createOrUpdate(input).pipe(
            tap((status: AlaStatus) => {
                this.ala.currentStep = status;
            }),
            map((status: AlaStatus) => null)
        );
    }

    getContactFiles(hasImmigratedLastFiveYearsId: string, caseId = this.caseId): Observable<GeneralDocumentGroupDto[]> {
        const input = new GetAlaDocumentsInput();
        input.step = AlaStatus.Contact;
        input.caseId = caseId;
        input.hasImmigratedLastFiveYearsId = hasImmigratedLastFiveYearsId;
        return this.documentService.getDocuments(new GetAlaDocumentsInput(input)).pipe(
            map((output: AlaDocumentsOutput) => output.documents)
        );
    }

    getContact(): Observable<AlaContactDto> {
        return this.alaContactServiceProxy.get(this.caseId);
    }

    createOrUpdateContact(input: CreateOrUpdateAlaContact): Observable<void> {
        this.applicationService.setStepToReview(AlaStatus.Contact);
        return this.alaContactServiceProxy.createOrUpdate(input).pipe(
            tap((status: AlaStatus) => {
                this.ala.currentStep = status;
            }),
            map((status: AlaStatus) => null)
        );
    }

    getAcademicTitles(): Observable<AlaAcademicTitleDto[]> {
        return this.alaAcademicTitleServiceProxy.getList(this.caseId);
    }

    createBlankAlaAcademicTitle(): Observable<number> {
        return this.alaAcademicTitleServiceProxy.createBlankAlaAcademicTitle(this.caseId);
    }

    deleteAcademicTitle(id: number): Observable<void> {
        return this.alaAcademicTitleServiceProxy.deleteAlaAcademicTitle(this.caseId, id);
    }

    createOrUpdateAcademicTitles(input: CreateOrUpdateAlaAcademicTitleInput): Observable<void> {
        this.applicationService.setStepToReview(AlaStatus.AcademicTitle);
        return this.alaAcademicTitleServiceProxy.createOrUpdateAlaAcademicTitle(input).pipe(
            tap((output: CreateOrUpdateAlaAcademicTitleOutput) => {
                this.ala.currentStep = output.status;
            }),
            map(() => null)
        );
    }

    getAcademicTitleFiles(subItemId: number, caseId = this.caseId): Observable<GeneralDocumentGroupDto[]> {
        const input = new GetAlaSubItemDocumentsInput();
        input.step = AlaStatus.AcademicTitle;
        input.caseId = caseId;
        input.subItemId = subItemId;
        return this.alaAcademicTitleServiceProxy.getAlaAcademicTitleFiles(new GetAlaSubItemDocumentsInput(input)).pipe(
            map((output: AlaDocumentsOutput) => output.documents)
        );
    }

    uploadAcademicTitleFiles(academicTitleId: number, input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.alaAcademicTitleServiceProxy.uploadFiles(academicTitleId, input);
    }

    getMedicalField(): Observable<AlaMedicalFieldDto> {
        return this.alaMedicalFieldServiceProxy.get(this.caseId);
    }

    createOrUpdateMedicalField(input: CreateOrUpdateAlaMedicalFieldInput): Observable<void> {
        this.applicationService.setStepToReview(AlaStatus.MedicalField);
        return this.alaMedicalFieldServiceProxy.createOrUpdateAlaMedicalField(input).pipe(
            tap((output: CreateOrUpdateAlaMedicalFieldOutput) => {
                this.ala.currentStep = output.status;
            }),
            map((output: CreateOrUpdateAlaMedicalFieldOutput) => null)
        );
    }

    getBusinesses(): Observable<AlaBusinessDto[]> {
        return this.alaBusinessServiceProxy.getList(this.caseId);
    }

    createOrUpdateBusiness(caseId: string, input: AlaBusinessDto[]): Observable<void> {
        this.applicationService.setStepToReview(AlaStatus.Business);
        return this.alaBusinessServiceProxy.createOrUpdate(caseId, input).pipe(
            tap((status: AlaStatus) => {
                this.ala.currentStep = status;
            }),
            map((status: AlaStatus) => null)
        );
    }

    getEmployments(): Observable<GetAlaEmploymentOutput> {
        return this.alaEmploymentServiceProxy.getList(this.caseId);
    }

    createOrUpdateEmployments(caseId: string, employments: AlaEmploymentDto[]): Observable<void> {
        this.applicationService.setStepToReview(AlaStatus.Employment);
        return this.alaEmploymentServiceProxy.createOrUpdate(new CreateOrUpdateAlaEmploymentInput({ caseId: caseId, employments: employments})).pipe(
            tap((status: AlaStatus) => {
                this.ala.currentStep = status;
            }),
            map((status: AlaStatus) => null)
        );
    }

    doesNotExceedMaxLevelOfAssistantEmployment(levelOfEmployment: number): Observable<boolean> {
        return this.alaEmploymentServiceProxy.doesNotExceedMaxLevelOfAssistantEmployment(this.caseId, levelOfEmployment);
    }

    getEmploymentFiles(caseId = this.caseId): Observable<GeneralDocumentGroupDto[]> {
        const input = new GetAlaDocumentsInput();
        input.step = AlaStatus.Employment;
        input.caseId = caseId;
        return this.documentService.getDocuments(new GetAlaDocumentsInput(input)).pipe(
            map((output: AlaDocumentsOutput) => output.documents)
        );
    }

    getInvoiceAddress(): Observable<GetAlaInvoiceAddressOutput> {
        return this.invoiceAddressService.get(this.caseId);
    }

    createOrUpdateInvoiceAddress(invoiceAddress: AlaInvoiceAddressDto): Observable<any> {
        this.applicationService.setStepToReview(AlaStatus.InvoiceAddress);
        return this.invoiceAddressService.createOrUpdate(this.caseId, invoiceAddress).pipe(tap((newStatusId: AlaStatus) => {
            this.ala.statusId = newStatusId;
        }));
    }

    getLanguageSkillFiles(languageSkillId: string, caseId = this.caseId): Observable<GeneralDocumentGroupDto[]> {
        const input = new GetAlaDocumentsInput();
        input.step = AlaStatus.LanguageSkill;
        input.caseId = caseId;
        input.languageSkillId = languageSkillId;
        return this.documentService.getDocuments(new GetAlaDocumentsInput(input)).pipe(
            map((output: AlaDocumentsOutput) => output.documents)
        );
    }

    getLanguageSkill(): Observable<AlaLanguageSkillDto> {
        return this.alaLanguageSkillServiceProxy.get(this.caseId);
    }

    createOrUpdateLanguageSkill(input: CreateOrUpdateAlaLanguageSkillInput): Observable<any> {
        this.applicationService.setStepToReview(AlaStatus.LanguageSkill);
        return this.alaLanguageSkillServiceProxy.createOrUpdate(input).pipe(tap((status: AlaStatus) => {
            this.ala.currentStep = status;
        }));
    }

    getPostgraduateTitles(): Observable<AlaPostgraduateTitleDto[]> {
        return this.alaPostgraduateTitleServiceProxy.getList(this.caseId);
    }

    createOrUpdatePostgraduateTitles(input: CreateOrUpdateAlaPostgraduateTitleInput): Observable<any> {
        this.applicationService.setStepToReview(AlaStatus.PostgraduateTitle);
        return this.alaPostgraduateTitleServiceProxy.createOrUpdateAlaPostgraduateTitles(input).pipe(tap((output: CreateOrUpdateAlaPostgraduateTitleOutput) => {
            this.ala.currentStep = output.status;
        }));
    }

    getRestrictionFiles(hasJudgementInSwissCriminalRecordId: string, caseId = this.caseId): Observable<GeneralDocumentGroupDto[]> {
        const input = new GetAlaDocumentsInput();
        input.step = AlaStatus.Restriction;
        input.caseId = caseId;
        input.hasJudgementInSwissCriminalRecordId = hasJudgementInSwissCriminalRecordId;
        return this.documentService.getDocuments(new GetAlaDocumentsInput(input)).pipe(
            map((output: AlaDocumentsOutput) => output.documents)
        );
    }

    getRestrictions(): Observable<AlaRestrictionOutput> {
        return this.alaRestrictionsServiceProxy.getRestrictions(this.caseId);
    }

    createOrUpdateAlaRestrictions(input: CreateOrUpdateAlaRestrictionsInput): Observable<any> {
        this.applicationService.setStepToReview(AlaStatus.Restriction);
        return this.alaRestrictionsServiceProxy.createOrUpdateAlaRestrictions(input).pipe(tap((status: AlaStatus) => {
            this.ala.currentStep = status;
        }));
    }

    getDiplomaFiles(isInEducationId: string, caseId = this.caseId): Observable<GeneralDocumentGroupDto[]> {
        const input = new GetAlaDocumentsInput();
        input.step = AlaStatus.Diploma;
        input.caseId = caseId;
        input.isInEducationId = isInEducationId;
        return this.documentService.getDocuments(new GetAlaDocumentsInput(input)).pipe(
            map((output: AlaDocumentsOutput) => output.documents)
        );
    }

    getDiploma(): Observable<AlaDiplomaDto> {
        return this.alaDiplomaServiceProxy.get(this.caseId);
    }

    createOrUpdateDiploma(input: CreateOrUpdateAlaDiploma): Observable<any> {
        this.applicationService.setStepToReview(AlaStatus.Diploma);
        return this.alaDiplomaServiceProxy.createOrUpdate(input).pipe(tap((status: AlaStatus) => {
            this.ala.currentStep = status;
        }));
    }

    createAddressDropdownList(contact: InvoiceAddressDetailDto, employments: AlaBusinessDto[], onlyPrivateAddressAllowed: boolean): SelectItem[] {

        let addresses: SelectItem[] = [];
        addresses.push({ value: AppConsts.codes.address.private, label: this.l('Address-Private') + ': ' + this.getPrivateAddressDescription(contact) });

        if (!onlyPrivateAddressAllowed) {
            employments?.forEach((employment) => {
                // Use employment id as item value
                addresses.push({ value: employment.id, label: this.l('EmploymentSingular') + ': ' + this.getEmploymentAddressDescription(employment) });
            });

            addresses.push({ value: AppConsts.codes.address.invoice, label: this.l(AppConsts.codes.address.invoice) });
        }

        return addresses;
    }

    private getPrivateAddressDescription(contact: InvoiceAddressDetailDto) {
        let str = contact?.street;

        if (contact?.streetNr && contact.streetNr.length > 0) {
            str = str.concat(' ');
            str = str.concat(contact.streetNr);
        }

        str = str?.concat(', ');
        str = str?.concat(contact?.city);

        return str;
    }

    private getEmploymentAddressDescription(employment: AlaBusinessDto) {
        let str = employment?.name;

        if (employment.street?.length > 0) {
            if (str.length > 0) {
                str = str.concat(', ');
            }

            str = str.concat(employment.street);

            if (employment.streetNr?.length > 0) {
                str = str.concat(' ');
                str = str.concat(employment.streetNr);
            }
        }

        if (employment.city?.length > 0) {
            if (str.length > 0) {
                str = str.concat(', ');
            }

            str = str.concat(employment.city);
        }

        return str;
    }

    checkDocuments(): Observable<AlaStatus[]> {
        switch (this.applicationService.type) {
            case ApplicationType.Alap:
                return this.alapService.validateThatAllMandatoryDocumentsHaveBeenUploaded(this.caseId);
        }
    }

    static getUrlForStep(step: AlaStatus): string {
        switch (step) {
            case AlaStatus.ApplicantPerson:
                return 'applicant-person';
            case AlaStatus.ApplicantSponsorship:
                return 'applicant-sponsorship';
            case AlaStatus.ApplicantContact:
                return 'applicant-contact';
            case AlaStatus.MedicalFieldDirector:
                return 'medical-field-director';
            case AlaStatus.Person:
                return 'person';
            case AlaStatus.Contact:
                return 'contact';
            case AlaStatus.Diploma:
                return 'diploma';
            case AlaStatus.LanguageSkill:
                return 'language-skill';
            case AlaStatus.PostgraduateTitle:
                return 'postgraduate-title';
            case AlaStatus.Restriction:
                return 'restriction';
            case AlaStatus.AcademicTitle:
                return 'academic-title';
            case AlaStatus.MedicalField:
                return 'medical-field';
            case AlaStatus.Business:
                return 'business';
            case AlaStatus.Employment:
                return 'employment';
            case AlaStatus.InvoiceAddress:
                return 'invoice-address';
            case AlaStatus.Release:
                return 'release';
            case AlaStatus.FinishReview:
                return 'finish-review';
            default:
                console.error('Invalid status for getUrlForStep');
                return null;
        }
    }

    getLegitimationPersonWithoutGln(legitimationId: number): Observable<LegitimationPersonWithoutRegisterDto>{
            return this.alapService.getLegitimationPersonWithoutRegister(legitimationId);
     }
}
